import { z } from 'zod';

/** What the agent of a station says to the server (ADR 0026): its links and the Meshtastic
 *  packets it heard. The same contract for the Windows service, the Electron app and the
 *  simulator. */

export const LINK_KINDS = [
  'fibre',
  'dsl',
  'cellular',
  'starlink',
  'satellite',
  'radio',
  'lan',
  'wifi',
  'other',
] as const;
export const linkKindSchema = z.enum(LINK_KINDS);
export type LinkKind = z.infer<typeof linkKindSchema>;

export const LINK_STATES = ['up', 'degraded', 'down'] as const;
export const linkStateSchema = z.enum(LINK_STATES);
export type LinkState = z.infer<typeof linkStateSchema>;

/** Where a link was read: a router the agent knows, or the PC measuring by itself. Open: an
 *  adapter added to the agent reports its own name. */
export const linkSourceSchema = z.string().trim().min(2).max(40);

export const linkReportSchema = z.object({
  /** Stable for the station: «wan1», «sim2», «starlink». */
  key: z.string().trim().min(1).max(40),
  kind: linkKindSchema,
  source: linkSourceSchema,
  label: z.string().trim().min(1).max(60),
  state: linkStateSchema,
  /** The link the station's traffic goes out of now. */
  active: z.boolean(),
  latencyMs: z.number().int().min(0).max(60_000).nullable(),
  downMbps: z.number().min(0).max(100_000).nullable(),
  upMbps: z.number().min(0).max(100_000).nullable(),
  /** Cellular signal (RSRP/RSSI) in dBm. */
  signalDbm: z.number().int().min(-200).max(0).nullable(),
  /** Starlink: share of the sky obstructed. */
  obstructionPct: z.number().min(0).max(100).nullable(),
  /** A short technical note from the router: operator, band, firmware. */
  detail: z.string().trim().max(120).nullable(),
});
export type LinkReport = z.infer<typeof linkReportSchema>;

export const MESH_TRANSPORTS = ['usb', 'ble', 'tcp'] as const;
export const meshTransportSchema = z.enum(MESH_TRANSPORTS);
export type MeshTransport = z.infer<typeof meshTransportSchema>;

const MAX_LINKS = 12;

export const agentHeartbeatSchema = z.object({
  agentVersion: z.string().trim().min(1).max(40),
  platform: z.string().trim().min(1).max(60),
  hostname: z.string().trim().min(1).max(120),
  /** When the agent measured the links (its clock): the server keeps its own receipt time. */
  measuredAt: z.string().datetime(),
  links: z.array(linkReportSchema).max(MAX_LINKS),
  mesh: z.object({ transport: meshTransportSchema.nullable(), connected: z.boolean() }),
});
export type AgentHeartbeat = z.infer<typeof agentHeartbeatSchema>;

/** One Meshtastic packet, as the agent or the MQTT reader normalise it. */
export const meshReportSchema = z.object({
  /** The node's number (`!1a2b3c4d` is its hex). */
  nodeNum: z.number().int().min(1).max(0xffffffff),
  /** With the node and the time, makes a packet sent twice (a queue flushed late) one. */
  packetId: z.number().int().min(0).max(0xffffffff).nullable(),
  /** When the radio heard it (CLAUDE.md §14), not when it reached the server. */
  heardAt: z.string().datetime(),
  name: z.string().trim().max(60).nullable().optional(),
  shortName: z.string().trim().max(8).nullable().optional(),
  hardware: z.string().trim().max(40).nullable().optional(),
  position: z
    .object({
      lng: z.number().min(-180).max(180),
      lat: z.number().min(-90).max(90),
      altitudeM: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  /** 0–100; above 100 the node is on external power. */
  batteryPct: z.number().int().min(0).max(101).nullable().optional(),
  voltage: z.number().min(0).max(30).nullable().optional(),
  snr: z.number().min(-50).max(50).nullable().optional(),
  rssi: z.number().int().min(-200).max(0).nullable().optional(),
  channelUtilization: z.number().min(0).max(100).nullable().optional(),
  text: z.string().max(240).nullable().optional(),
});
export type MeshReport = z.infer<typeof meshReportSchema>;

const MAX_BATCH = 500;

export const meshBatchSchema = z.object({
  reports: z.array(meshReportSchema).min(1).max(MAX_BATCH),
});
export type MeshBatch = z.infer<typeof meshBatchSchema>;

export const BANDWIDTH_LEVELS = ['full', 'reduced', 'minimal', 'offline'] as const;
export const bandwidthLevelSchema = z.enum(BANDWIDTH_LEVELS);
export type BandwidthLevel = z.infer<typeof bandwidthLevelSchema>;

export const agentHeartbeatReplySchema = z.object({
  station: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    missionId: z.string().uuid().nullable(),
  }),
  level: bandwidthLevelSchema,
  serverTime: z.string().datetime(),
  /** How often the agent should call again. */
  heartbeatSeconds: z.number().int(),
});
export type AgentHeartbeatReply = z.infer<typeof agentHeartbeatReplySchema>;

export const meshBatchReplySchema = z.object({
  accepted: z.number().int(),
  duplicates: z.number().int(),
});
export type MeshBatchReply = z.infer<typeof meshBatchReplySchema>;

const MAX_NOTES = 200;

/** A line of chronicle written at the station while it was offline (ADR 0027): its own id makes
 *  it one line however many times it is sent, its own time is when it was written (§14). */
export const agentNoteSchema = z.object({
  id: z.string().uuid(),
  at: z.string().datetime(),
  operator: z.string().trim().min(2).max(60),
  text: z.string().trim().min(2).max(1000),
});
export type AgentNote = z.infer<typeof agentNoteSchema>;

export const agentNotesSchema = z.object({ notes: z.array(agentNoteSchema).min(1).max(MAX_NOTES) });

export const agentNotesReplySchema = z.object({
  accepted: z.number().int(),
  duplicates: z.number().int(),
  /** Notes for a mission closed meanwhile, or a station with no open mission: its chronicle is
   *  sealed or absent, the note stays on the station. */
  skipped: z.number().int(),
});
export type AgentNotesReply = z.infer<typeof agentNotesReplySchema>;

export const PROBE_MAX_KB = 1024;
export const agentProbeQuerySchema = z.object({
  kb: z.coerce.number().int().min(1).max(PROBE_MAX_KB),
});
